import React, { useContext, useEffect, useMemo, useState } from 'react';
import MuYiTongXueLogoImage from './assets/muyitongxue-logo.png';
import JingguiLogoImage from '../../assets/jinggui-logo.png';
import YellowStarPNG from './assets/yellow-star.png';
import RedStarSVG from './assets/red-star.svg?react';
import { useQuery } from '@apollo/client';
import { GetTranscriptDataDocument, SubmissionStatus } from '../../services/graphql/types/graphql';
import { Loader } from '../../components/Loader';
import { CasdoorContext } from '../App/casdoor.context';
import { IdentityContext } from '../App/identity.context';
import { useStoreContext } from '../../factorys/useStoreContext';
import { isJingguiOrg } from '../../config/org';
import { keyBy, sum } from 'lodash';
import cx from 'classnames';
import * as Sentry from '@sentry/react';
import './styles.scss';
import { AssignmentMediaPreview } from '../../components/AssignmentMediaPreview';
import {
  getAssignmentMediaTypeHint,
  resolveMediasImageFirst,
  sortMediasImageFirst,
  type MediaWithUrl,
} from '../../utils/media';

const secureMediaUrl = (url: string) => url.replace(/^http:/, 'https:');

const gradeToStar = (grade: number) => {
  switch (grade) {
    case 90:
      return 5;
    case 80:
      return 4;
    case 70:
      return 3;
    case 60:
      return 2;
    default:
      return 0;
  }
};

const scoreToLetter = (score: number) => {
  if (score >= 90) return 'A';
  if (score >= 85) return 'A-';
  if (score >= 80) return 'B';
  if (score >= 75) return 'B-';
  if (score >= 70) return 'C';
  return 'C-';
};

const assignmentRubricWeightPercent = (weight: number) => Math.round(weight * 100);

const parseSubmissionCreateAtMs = (raw: string) => {
  const n = parseInt(raw, 10);
  if (n > 0) return n;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const Transcript: React.FC<{
  submissionId: string;
  minimal?: boolean;
  info?: boolean;
  embedded?: boolean;
  onClose?: () => void;
}> = ({ submissionId, minimal = false, info = false, embedded = false, onClose }) => {
  const { user } = useContext(CasdoorContext);
  const { currentSemester } = useStoreContext(IdentityContext);
  const brandLogo = isJingguiOrg() ? JingguiLogoImage : MuYiTongXueLogoImage;
  const [mainArtworkIndex, setMainArtworkIndex] = useState(0);
  const { data, loading, error } = useQuery(GetTranscriptDataDocument, {
    variables: { submissionId },
  });

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  useEffect(() => {
    setMainArtworkIndex(0);
  }, [submissionId]);

  const sortedAssignmentRubrics = useMemo(() => {
    const rubrics = data?.submission.assignment.assignmentRubrics ?? [];
    return [...rubrics].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [data]);

  const taRubrics = useMemo(
    () => sortedAssignmentRubrics.filter(({ rubric }) => rubric.graderRoleType === 'TA'),
    [sortedAssignmentRubrics],
  );

  const teacherRubrics = useMemo(
    () => sortedAssignmentRubrics.filter(({ rubric }) => rubric.graderRoleType === 'Teacher'),
    [sortedAssignmentRubrics],
  );

  const creditByRubricId = useMemo(() => {
    return keyBy(data?.submission.credits ?? [], 'rubric.rubricId');
  }, [data]);

  const taCredits = useMemo(() => {
    return (data?.submission.credits ?? []).filter(({ rubric }) => rubric.graderRoleType === 'TA');
  }, [data]);

  const teacherCredits = useMemo(() => {
    return (data?.submission.credits ?? []).filter(
      ({ rubric }) => rubric.graderRoleType === 'Teacher',
    );
  }, [data]);

  const totalGrade = useMemo(() => {
    if (!data) {
      return 0;
    }

    const assignmentRubrics = keyBy(
      data.submission.assignment.assignmentRubrics,
      'rubric.rubricId',
    );

    const creditGrades = data.submission.credits.map(({ rubric, rubricLevel }) => {
      const rubricId = rubric.rubricId;
      const rubricLevelId = rubricLevel.rubricLevelId;
      if (!rubricId || !rubricLevelId) {
        return 0;
      }
      const rubricLevelGrade = assignmentRubrics[rubricId].rubric?.rubricLevels.find(
        (rl) => rl.rubricLevelId === rubricLevelId,
      )?.grade;

      return assignmentRubrics[rubricId].weight * (rubricLevelGrade ?? 0);
    });

    return sum(creditGrades) + (data.submission.gradeOffset ?? 0);
  }, [data]);

  const letterGrade = useMemo(() => scoreToLetter(totalGrade), [totalGrade]);

  const artworkMedias = useMemo((): MediaWithUrl[] => {
    if (!data?.submission?.artworks) {
      return [];
    }
    return data.submission.artworks.flatMap((artwork) =>
      (artwork.medias ?? []).map((media) => ({
        url: media.url,
        mediaId: media.mediaId,
      })),
    );
  }, [data]);

  const [sortedArtworkMedias, setSortedArtworkMedias] = useState(() =>
    sortMediasImageFirst(artworkMedias),
  );
  const artworkMediaKey = useMemo(
    () => artworkMedias.map((m) => m.mediaId ?? m.url).join('|'),
    [artworkMedias],
  );

  useEffect(() => {
    let cancelled = false;
    setSortedArtworkMedias(sortMediasImageFirst(artworkMedias));
    resolveMediasImageFirst(artworkMedias).then((sorted) => {
      if (!cancelled) {
        setSortedArtworkMedias(sorted);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [artworkMediaKey, artworkMedias]);

  const authorName = data?.submission.authors[0]?.name ?? '';
  const className = data?.submission.authors[0]?.userIdentity[0]?.class?.name ?? '';
  const semesterName = currentSemester?.name ?? '';
  const classSemesterLine = useMemo(() => {
    if (className && semesterName) {
      return `${className} · ${semesterName}`;
    }
    return className || semesterName;
  }, [className, semesterName]);

  const courseName =
    data?.submission.assignment.lesson?.course.name.replace(/_/g, ' ') ?? '';
  const lessonName = data?.submission.assignment.lesson?.name ?? '';
  const assignmentFullName = data?.submission.assignment.name ?? '';
  const assignmentSuffix = useMemo(() => {
    const idx = assignmentFullName.indexOf('：');
    if (idx < 0) return '';
    return assignmentFullName
      .substring(idx + 1)
      .replace(/《/g, '')
      .replace(/》/g, '');
  }, [assignmentFullName]);

  const descriptionLines = useMemo(() => {
    const desc = data?.submission.assignment.description ?? '';
    return desc
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }, [data]);

  const completionDateDisplay = useMemo(() => {
    const raw = data?.submission.createAt;
    if (!raw) return '—';
    const ms = parseSubmissionCreateAtMs(raw);
    if (ms <= 0) return '—';
    const d = new Date(ms);
    return `${d.getMonth() + 1}.${d.getDate()}`;
  }, [data]);

  const minQuantityDisplay = useMemo(() => {
    const mq = data?.submission.assignment.minQuantity;
    return mq != null ? String(mq) : '—';
  }, [data]);

  const facultyName = data?.submission.assignment.lesson?.faculty?.name ?? '';
  const extraComment = data?.submission.extraComment ?? '';

  const taDisplayItems = useMemo(() => {
    return taRubrics
      .map((ar) => {
        const rubricId = ar.rubric.rubricId;
        const credit = rubricId ? creditByRubricId[rubricId] : undefined;
        if (!credit || !ar.rubric.name) return null;
        return { name: ar.rubric.name };
      })
      .filter((item): item is { name: string } => item != null);
  }, [taRubrics, creditByRubricId]);

  const taWeightPercentSum = useMemo(
    () => taRubrics.reduce((acc, ar) => acc + assignmentRubricWeightPercent(ar.weight), 0),
    [taRubrics],
  );

  if (loading || !data || !user) {
    return (
      <div className={cx('transcript', embedded ? 'transcript--embedded' : 'transcript--modal')}>
        <div className="transcript-loading">
          <Loader source="Transcript" />
        </div>
      </div>
    );
  }

  if (info) {
    if (data.submission.submissionStatus === SubmissionStatus.CompletedButNoGrade) {
      return <div className="text-[#F0403C] text-3xl mt-auto text-right w-full">未完成</div>;
    }

    return (
      <div className="w-full grade-info flex flex-col">
        <div className="flex space-x-3">
          {taCredits?.map((credit) => {
            return <RedStarSVG key={JSON.stringify(credit)} className="w-5 h-5"></RedStarSVG>;
          })}
        </div>
        <div className="mt-4 flex flex-col w-full space-y-2 text-xs">
          {teacherCredits?.map((c, iTc) => {
            return (
              <div className="flex" key={JSON.stringify(c)}>
                <div className="line-clamp-1">{c.rubric.name}</div>
                <div className="flex ml-3">
                  {Array(gradeToStar(c.rubricLevel?.grade ?? 0))
                    .fill(0)
                    .map((_, iStar) => {
                      return (
                        <div key={`${iTc}-${iStar}`} className="w-[14px] h-[14px]">
                          <img src={YellowStarPNG} alt="" />
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (minimal) {
    return (
      <div className="pl-6 pr-7 w-full h-full relative">
        <div className="pt-1 border-b border-[#F0403C] text-[#F0403C] flex space-x-4">
          {taCredits?.map((credit) => {
            return (
              <div
                className="flex flex-col justify-center items-center py-2"
                key={JSON.stringify(credit)}
              >
                <RedStarSVG className="w-5 h-5"></RedStarSVG>
                <div className="text-xs leading-4">{credit.rubricLevel?.text}</div>
              </div>
            );
          })}
        </div>
        <div className="text-black py-2 border-[#F0403C] border-b text-[3.9vh] leading-8 tracking-[4px]">
          {totalGrade}
        </div>
        <div className="mt-2">
          <div className="text-xs leading-[185.714%] tracking-[0.136vh]">
            {teacherCredits?.map((c) => {
              return (
                <React.Fragment key={JSON.stringify(c)}>
                  <span className="text-[#797979]">{c.rubricLevel?.text}</span>
                  <br />
                </React.Fragment>
              );
            })}
            <span className="text-[#F0403C]">{extraComment}</span>
          </div>
        </div>
        <div className="absolute left-1/2 bottom-1 -translate-x-1/2 space-x-2 flex">
          {[1, 2, 3].map((_, i) => {
            return <div key={i} className="w-1 h-1 rounded-full bg-[#c5c5c5]"></div>;
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={cx('transcript', embedded ? 'transcript--embedded' : 'transcript--modal')}>
      <div className="transcript-hdr-beige">
        <div className="transcript-hdr-left">
          <div className="transcript-hdr-name">{authorName}</div>
          {classSemesterLine && <div className="transcript-hdr-meta">{classSemesterLine}</div>}
        </div>
        <img className="transcript-hdr-logo" src={brandLogo} alt="" />
        {onClose && (
          <button
            type="button"
            className="transcript-hdr-close"
            aria-label="关闭"
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 5L15 15M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="transcript-scroll">
        <div className="transcript-asg-meta">
          <div className="transcript-asg-tag-row">
            {courseName && (
              <div className="transcript-asg-tag">
                <span className="transcript-asg-tag-txt">{courseName}</span>
              </div>
            )}
            <div className="transcript-asg-lesson-line">
              {lessonName} {assignmentSuffix}
            </div>
          </div>
          {assignmentSuffix && <div className="transcript-asg-title">{assignmentSuffix}</div>}
          {descriptionLines.map((line, idx) => (
            <div key={idx} className="transcript-asg-desc-row">
              <div className="transcript-asg-desc">{line}</div>
            </div>
          ))}
          <div className="transcript-asg-stats-row">
            <div className="transcript-asg-stat-item">
              <div className="transcript-asg-stat-label-wrap">
                <span className="transcript-asg-stat-label">完成</span>
                <span className="transcript-asg-stat-label">日期</span>
              </div>
              <span className="transcript-asg-stat-value">{completionDateDisplay}</span>
            </div>
            <div className="transcript-asg-stat-item transcript-asg-stat-item--gap">
              <div className="transcript-asg-stat-label-wrap">
                <span className="transcript-asg-stat-label">最低</span>
                <span className="transcript-asg-stat-label">数量</span>
              </div>
              <span className="transcript-asg-stat-value">{minQuantityDisplay}</span>
            </div>
          </div>
        </div>

        {sortedArtworkMedias.length > 0 && (
          <div className="transcript-gallery">
            <div className="transcript-gallery-main">
              <AssignmentMediaPreview
                url={secureMediaUrl(
                  (sortedArtworkMedias[mainArtworkIndex] ?? sortedArtworkMedias[0]).url,
                )}
                thumbnail={false}
                embedded
                mediaTypeHint={getAssignmentMediaTypeHint(
                  secureMediaUrl(
                    (sortedArtworkMedias[mainArtworkIndex] ?? sortedArtworkMedias[0]).url,
                  ),
                  'full',
                )}
              />
            </div>
            {sortedArtworkMedias.length > 1 && (
              <div className="transcript-gallery-thumbs">
                {sortedArtworkMedias.map((media, idx) => (
                  <div
                    key={media.mediaId ?? media.url}
                    className={cx('transcript-gallery-thumb-wrap', {
                      'transcript-gallery-thumb-wrap--active': idx === mainArtworkIndex,
                    })}
                    onClick={() => setMainArtworkIndex(idx)}
                  >
                    <AssignmentMediaPreview
                      url={secureMediaUrl(media.url)}
                      thumbnail
                      embedded
                      mediaTypeHint={getAssignmentMediaTypeHint(
                        secureMediaUrl(media.url),
                        'thumbnail',
                      )}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="transcript-eval-block">
          <div className="transcript-eval-heading">评价维度及在总评中占比</div>

          {taRubrics.length > 0 && (
            <div className="transcript-eval-row">
              <div className="transcript-eval-label-wrap">
                <span className="transcript-eval-name">作业完成度：</span>
                <div className="transcript-ta-items">
                  {taDisplayItems.map((item) => (
                    <div key={item.name} className="transcript-ta-item">
                      <span className="transcript-ta-item-name">{item.name}</span>
                      <img className="transcript-star-on" src={YellowStarPNG} alt="" />
                    </div>
                  ))}
                </div>
              </div>
              <span className="transcript-eval-pct">{taWeightPercentSum}%</span>
            </div>
          )}

          {teacherRubrics.map((ar) => {
            const rubricId = ar.rubric.rubricId;
            const credit = rubricId ? creditByRubricId[rubricId] : undefined;
            const starCount = gradeToStar(credit?.rubricLevel?.grade ?? 0);
            return (
              <div key={rubricId} className="transcript-eval-row">
                <div className="transcript-eval-label-wrap">
                  <span className="transcript-eval-name">{ar.rubric.name}：</span>
                  <div className="transcript-stars">
                    {Array.from({ length: starCount }).map((_, iStar) => (
                      <img key={iStar} className="transcript-star-on" src={YellowStarPNG} alt="" />
                    ))}
                  </div>
                </div>
                <span className="transcript-eval-pct">
                  {assignmentRubricWeightPercent(ar.weight)}%
                </span>
              </div>
            );
          })}

          {extraComment && (
            <div className="transcript-eval-row">
              <span className="transcript-eval-label">{extraComment}</span>
            </div>
          )}

          <div className="transcript-eval-footer">
            <span className="transcript-final-grade">总评：{letterGrade}</span>
            {facultyName && <span className="transcript-faculty">任课教师：{facultyName}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};
